import { initTracker } from '@agently/utils';
initTracker();

import '@fontsource-variable/geist';

import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router/dom';
import { createRouter } from 'router';
import { PwaUpdateProvider } from './pwa/PwaUpdateContext';
import { PwaUpdater } from './pwa/PwaUpdater';
import AppProviders from './providers/AppProviders';
import { applyCachedBrandbook } from './stores/brandbook';

import './index.css';

import Landing from 'components/Landing';

import { addLoomPlayer } from '@agently/utils';

const PWA_PROTOCOL_REDIRECT_PARAM = 'pwaprotocolredirect';
const PWA_PROTOCOLS = new Set(['web+agently:', 'web+pwa:']);

function getPWADisplayMode() {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  if (document.referrer.startsWith('android-app://')) {
    return 'twa';
  } else if ((navigator as any).standalone || isStandalone) {
    return 'standalone';
  }
  return 'browser';
}

function shouldRenderApp() {
  const hasAuthToken = Boolean(localStorage.getItem('authToken'));
  const displayMode = getPWADisplayMode();

  return (
    displayMode === 'standalone' ||
    displayMode === 'twa' ||
    import.meta.env.DEV ||
    window.location.host.includes('dev') ||
    hasAuthToken
  );
}

function getPathFromPwaProtocolRedirect(value: string) {
  const redirect = value.trim();
  if (!redirect) {
    return null;
  }

  try {
    const url = new URL(redirect);
    if (!PWA_PROTOCOLS.has(url.protocol)) {
      return null;
    }

    const pathname = url.hostname
      ? `/${url.hostname}${url.pathname === '/' ? '' : url.pathname}`
      : !url.pathname
        ? '/'
        : url.pathname.startsWith('/')
          ? url.pathname
          : `/${url.pathname}`;

    return `${pathname}${url.search}${url.hash}`;
  } catch {
    if (redirect.includes(':') || redirect.startsWith('//')) {
      return null;
    }

    return redirect.startsWith('/') ? redirect : `/${redirect}`;
  }
}

function normalizePwaProtocolRedirect() {
  const searchParams = new URLSearchParams(window.location.search);
  const redirect = searchParams.get(PWA_PROTOCOL_REDIRECT_PARAM);
  if (!redirect) {
    return;
  }

  const nextUrl = getPathFromPwaProtocolRedirect(redirect);
  if (!nextUrl) {
    searchParams.delete(PWA_PROTOCOL_REDIRECT_PARAM);
    const search = searchParams.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
    );
    return;
  }

  window.history.replaceState(null, '', nextUrl);
}

const container = document.getElementById('root');
const root = createRoot(container as Element, {
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
});

(function () {
  applyCachedBrandbook();
  addLoomPlayer();
  normalizePwaProtocolRedirect();
  const router = createRouter();

  root.render(
    <PwaUpdateProvider>
      <PwaUpdater />
      {shouldRenderApp() ? (
        <AppProviders>
          <RouterProvider router={router} />
        </AppProviders>
      ) : (
        <Landing />
      )}
    </PwaUpdateProvider>,
  );
})();
